import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/compat/router";
import { useInstantSearch } from "react-instantsearch-hooks-web";
import { dequal } from "dequal";

// Capitalizes each word (preserving spaces)
export function capitalizeFacet(value) {
  if (!value) return value;
  return value
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Decodes URL params and formats them to match Algolia facet values
export function decodeParam(param, refinementKey) {
  if (!param) return [];

  return param.split(",").map((item) => {
    const decodedItem = item.replace(/\+/g, " "); // Convert "+" to spaces

    if (refinementKey === "root_types" && decodedItem.includes(":")) {
      const [prefix, suffix] = decodedItem.split(":");
      const capitalizedSuffix = capitalizeFacet(suffix);
      return `${prefix}:${capitalizedSuffix}`;
    }

    if (refinementKey === "feelings") {
      return decodedItem.charAt(0).toUpperCase() + decodedItem.slice(1).toLowerCase();
    }

    return refinementKey === "brand" ? item : decodedItem; // **Do NOT transform brand**
  });
}


function removeUndefinedParams(params) {
  return Object.entries(params).reduce((queries, [key, value]) => {
    if (value != null) {
      return { ...queries, [key]: value };
    }
    return queries;
  }, {});
}

function urlToParams(url) {
  const query = {};
  new URL(url).searchParams.forEach((value, key) => {
    query[key] = decodeURIComponent(value);
  });
  return query;
}

function NextRouterHandler({
  dynamicRouteQuery = {},
  routeToState,
  stateToRoute,
  writeDelay,
}) {
  const router = useRouter();
  const { use, setUiState } = useInstantSearch();
  const [isLeaving, setIsLeaving] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [stableQuery, setStableQuery] = useState(router?.query || {});

  if (!dequal(stableQuery, router?.query || {})) {
    setStableQuery(router?.query || {});
  }

  useEffect(() => {
    function onRouteChangeStart(url) {
      setIsLeaving(true);
    }

    router.events.on("routeChangeStart", onRouteChangeStart);
    return () => router.events.off("routeChangeStart", onRouteChangeStart);
  }, [router]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;
    const uiState = routeToState(stableQuery);
    setUiState(uiState);
  }, [stableQuery, isHydrated]);

  useEffect(() => {
    if (isLeaving || !isHydrated) return;
    let timeoutId;
    return use(() => ({
      onStateChange({ uiState }) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          const routeQuery = removeUndefinedParams(stateToRoute(uiState));
          const pathname = dynamicRouteQuery.category
            ? `/shop/${dynamicRouteQuery.category}`
            : router.pathname;
          const search = new URLSearchParams(routeQuery).toString();
          router.push(
            {
              pathname,
              search: search ? `?${decodeURIComponent(search)}` : "",
            },
            undefined,
            { shallow: true }
          );
        }, writeDelay);
      },
    }));
  }, [isHydrated]);

  return null;
}

export function useNextRouterHandler({
  dynamicRouteQuery = {},
  routeToState,
  stateToRoute,
  url,
  writeDelay = 10,
}) {
  const [stableDynamicRouteQuery, setStableDynamicRouteQuery] =
    useState(dynamicRouteQuery);

  if (!dequal(stableDynamicRouteQuery, dynamicRouteQuery)) {
    setStableDynamicRouteQuery(dynamicRouteQuery);
  }

  return {
    initialUiState: routeToState(urlToParams(url)),
    NextRouterHandler: useCallback(
      () =>
        NextRouterHandler({
          dynamicRouteQuery,
          routeToState,
          stateToRoute,
          writeDelay,
        }),
      [stableDynamicRouteQuery]
    ),
  };
}
